import type { ApolloClient } from 'apollo-client';

import { type FeatureId, type ITFGAnalyticsCallbackParams } from '../../common/types';
import { useGetInTrialFeatureGateBannerData } from '../use-get-user-tenant-experience-data';

import { type IsValidAudienceData } from './types';

export const useIsValidAudience = ({
	// Apps have different ways of getting the AGG client. Its not always as simple as useClient() and depends on the app's architecture.
	// Therefore, we need to pass it in as a prop.
	aggClient,
	tenantId,
	sendOperationalEvent,
	featureId,
	skip = false,
}: {
	aggClient: ApolloClient<any>;
	sendOperationalEvent: (params: ITFGAnalyticsCallbackParams) => void;
	tenantId: string;
	featureId: FeatureId;
	skip?: boolean;
}): IsValidAudienceData => {
	const { error, hasPaymentDetailsOnFile, canUserPayAndUpgrade, daysLeftInTrial, loading } =
		useGetInTrialFeatureGateBannerData({
			aggClient,
			sendOperationalEvent,
			skip,
			tenantId,
			featureId,
		});

	if (error || skip) {
		return {
			isValidAudience: false,
			data: {
				hasPaymentDetailsOnFile: false,
				canUserPayAndUpgrade: false,
				daysLeftInTrial: undefined,
			},
			isLoading: false,
		};
	}

	const isValidAudience =
		daysLeftInTrial !== undefined &&
		daysLeftInTrial > 0 &&
		!Boolean(hasPaymentDetailsOnFile) &&
		Boolean(canUserPayAndUpgrade);

	return {
		isValidAudience,
		// Returning the same data as useGetInTrialFeatureGateBannerData to allow us to leverage the same data for exposure analytics
		data: {
			hasPaymentDetailsOnFile,
			canUserPayAndUpgrade,
			daysLeftInTrial,
		},
		isLoading: loading,
	};
};
